/**
 * One place for every approval (owner, 17 Oct 2026: "Create a dedicated
 * approval section for surbhi for all the approvals coming in and for
 * everybody").
 *
 * Each kind keeps its own rules, its own answer and its own guard — who may
 * approve a transfer is a switch on the person, who answers an order is the
 * counter's ASM, leave belongs to whoever keeps the register. The section
 * only gathers what waits for a person's answer, across every book they
 * open, and what that person has asked of others.
 */

export const APPROVAL_KINDS = ['companyExpenses', 'returns', 'dealers', 'transfers', 'orders', 'leave', 'expenses', 'showrooms', 'enrolments', 'verifications', 'tasks'] as const;
export type ApprovalKind = (typeof APPROVAL_KINDS)[number];

export const APPROVAL_KIND_LABELS: Record<ApprovalKind, string> = {
  // Paid by the office straight onto a supplier's QR; over ₹50,000 Sunny first (31 Oct 2026).
  companyExpenses: 'Company expenses',
  // Rahul's, then Neeraj's with the return bill (30 Oct 2026).
  returns: 'Returns',
  // Sunny's: a dealer the team has brought to him (27 Oct 2026).
  dealers: 'New dealers',
  transfers: 'Vehicle transfers',
  orders: 'Orders',
  leave: 'Leave',
  expenses: 'Expenses',
  showrooms: 'Showroom checks',
  enrolments: 'New employees',
  verifications: 'Aadhaar checks',
  tasks: 'Task checks',
};

export interface ApprovalsSummary {
  /** Waiting for this person's answer, by kind — only the kinds they answer. */
  waiting: Partial<Record<ApprovalKind, number>>;
  /** The figure the rail and the reminder bar show. Task checks are left out: /tasks carries its own count. */
  total: number;
  /**
   * Waiting on somebody else, which this person can see but not answer:
   * orders with their counter's ASM, transfers with the receiving mitr.
   */
  watching: { orders: number; transfers: number };
  /** This person's own asks still waiting for somebody's answer. */
  asked: number;
}

/**
 * Who has the section: every sign-in but a counter's. A mitra, outlet or
 * dealer answers its transfers and follows its orders on its own home.
 */
export function hasApprovalsSection(user: { roleKey?: string | null; role?: { key: string } | null } | null | undefined): boolean {
  if (!user) return false;
  const key = user.roleKey ?? user.role?.key ?? null;
  // Not the TVS dispatch hand either (owner, 19 Oct 2026: "in sunil dispatch portal remove approvals section"):
  // he answers nothing, and his portal is the dispatch and the store it empties.
  return Boolean(key) && key !== 'BUYER' && key !== 'TVS_DISPATCH';
}
